import { Box, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AppleTvIcon from '../../assets/icons/icon-apple-tv-dark.svg';
import MobileIcon from '../../assets/icons/icon-mobile-dark.svg';
import TabletIcon from '../../assets/icons/icon-tablet-dark.svg';
import LaptopIcon from '../../assets/icons/icon-laptop-dark.svg';
import SmartTvIcon from '../../assets/icons/icon-smart-tv-dark.svg';
import MagIcon from '../../assets/icons/icon-mag-dark.svg';

import SonyLogo from '../../assets/images/sony-dark.svg';
import VizioLogo from '../../assets/images/vizio-dark.svg';
import XboxLogo from '../../assets/images/xbox-dark.svg';
import SamsungLogo from '../../assets/images/samsung-dark.svg';
import LgLogo from '../../assets/images/lg-dark.svg';
import RokuLogo from '../../assets/images/roku-dark.svg';
import FireTvLogo from '../../assets/images/fire-tv-dark.svg';
import PlaystationLogo from '../../assets/images/playstation-dark.svg';
import XfinityLogo from '../../assets/images/xfinity-dark.svg';
import GoogleTvLogo from '../../assets/images/google-tv-dark.svg';

const LogoSection = () => {
  const logos = [
    {
      name: 'Sony',
      logo: SonyLogo,
      width: '100px',
    },
    {
      name: 'Vizio',
      logo: VizioLogo,
      width: '100px',
    },
    {
      name: 'Xbox',
      logo: XboxLogo,
      width: '100px',
    },
    {
      name: 'Samsung',
      logo: SamsungLogo,
      width: '100px',
    },
    {
      name: 'LG',
      logo: LgLogo,
      width: '80px',
    },
    {
      name: 'Roku',
      logo: RokuLogo,
      width: '80px',
    },
    {
      name: 'Fire TV',
      logo: FireTvLogo,
      width: '90px',
    },
    {
      name: 'Playstation',
      logo: PlaystationLogo,
      width: '170px',
    },
    {
      name: 'Xfinity',
      logo: XfinityLogo,
      width: '100px',
    },
    {
      name: 'Google TV',
      logo: GoogleTvLogo,
      width: '170px',
    },
  ];

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
      }}
    >
      {logos.map((logo, i) => (
        <img
          key={i}
          src={logo.logo}
          alt={`${logo.name} logo`}
          style={{ width: logo.width, height: 'auto' }}
          loading="lazy"
        />
      ))}
    </Stack>
  );
};

export default function Section6() {
  const { t } = useTranslation();

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column' },
        justifyContent: 'center',
        alignItems: 'center',
        py: { xs: 8, md: 10 },
        gap: '80px',
      }}
      maxWidth="lg"
    >
      <Stack gap={2} sx={{ width: '100%' }}>
        <Stack component={'header'} gap={1}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '32px', md: '40px' },
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {t('HOME_PAGE.SECTION6.TITLE')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: '16px',
              fontWeight: '300',
              textAlign: 'center',
            }}
          >
            {t('HOME_PAGE.SECTION6.PARAGRAPH')}
          </Typography>
        </Stack>
        <Stack
          sx={{
            display: 'flex',
            gap: 4,
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
            flexWrap: 'wrap',
            mt: '24px',
            '& .MuiStack-root': {
              display: 'flex',
              gap: 1.5,
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '100px',
              maxHeight: '100px',
            },
          }}
        >
          <Stack>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                backgroundImage: `url(${AppleTvIcon})`,
                backgroundSize: '40px 40px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'primary.neutral1000',
                fontSize: '16px',
                fontWeight: '400',
              }}
            >
              {t('HOME_PAGE.SECTION6.APPLE_TV')}
            </Typography>
          </Stack>

          <Stack>
            <Box
              sx={{
                width: '56px',
                height: '28px',
                backgroundImage: `url(${MobileIcon})`,
                backgroundSize: '56px 28px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'primary.neutral1000',
                fontSize: '16px',
                fontWeight: '400',
              }}
            >
              {t('HOME_PAGE.SECTION6.MOBILE')}
            </Typography>
          </Stack>

          <Stack>
            <Box
              sx={{
                width: '56px',
                height: '42px',
                backgroundImage: `url(${TabletIcon})`,
                backgroundSize: '56px 42px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'primary.neutral1000',
                fontSize: '16px',
                fontWeight: '400',
              }}
            >
              {t('HOME_PAGE.SECTION6.TABLET')}
            </Typography>
          </Stack>

          <Stack>
            <Box
              sx={{
                width: '92px',
                height: '56px',
                backgroundImage: `url(${LaptopIcon})`,
                backgroundSize: '92px 56px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'primary.neutral1000',
                fontSize: '16px',
                fontWeight: '400',
              }}
            >
              {t('HOME_PAGE.SECTION6.LAPTOP')}
            </Typography>
          </Stack>

          <Stack>
            <Box
              sx={{
                width: '63px',
                height: '56px',
                backgroundImage: `url(${SmartTvIcon})`,
                backgroundSize: '63px 56px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'primary.neutral1000',
                fontSize: '16px',
                fontWeight: '400',
              }}
            >
              {t('HOME_PAGE.SECTION6.SMART_TV')}
            </Typography>
          </Stack>

          <Stack>
            <Box
              sx={{
                width: '50px',
                height: '34px',
                backgroundImage: `url(${MagIcon})`,
                backgroundSize: '50px 34px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'primary.neutral1000',
                fontSize: '16px',
                fontWeight: '400',
              }}
            >
              {t('HOME_PAGE.SECTION6.MAG')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Typography
        variant="body1"
        sx={{
          color: 'primary.neutral800',
          fontSize: '28px',
          fontWeight: '600',
          width: { xs: '100%', sm: 'calc(100% / 2)', md: 'calc(100% / 3)' },
          textAlign: 'center',
        }}
      >
        {t('HOME_PAGE.SECTION6.SECOND_PARAGRAPH')}
      </Typography>
      <LogoSection />
    </Container>
  );
}
